import { useCallback, useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';
import {
 Heading,
 Flex,
 Box,
 Button,
 Text,
 useToast,
} from '@chakra-ui/react';
import baseService from '../../services/base';
import { AttachEvidenceModal, EditStatusModal } from '../../components/Modals';
import { QuestionCardSimple, PillarSelectorRow } from '../../components/Card';
import { DownloadResponsesButton, EvidenceDownloadButton } from '../../components/Button';
import StatusTag from '../../components/Tags/StatusTag';
import { useSSO } from '../../context/SSO';
import RespondentViewPillar from '../../components/Survey/RespondentViewPillar';
import { initializeDefaultValues } from './certifications/defaultValues';
import { respostaComConteudo } from '../../helpers/medidasGri';
import { useResponsesDownload } from '../../hooks/useResponsesDownload';
import useAuditHeaderData, { useIsAudit } from '../audit/useAuditHeaderData';

class APIError extends Error {
 constructor(message, status) {
  super(message);
  this.name = 'APIError';
  this.status = status;
 }
}

async function fetchData(url) {
 try {
  const response = await baseService.get(url);
  if (!response?.data || Object.keys(response?.data)?.length === 0) {
   throw new APIError('No data available', 404);
  }
  return response.data;
 } catch (error) {
  if (error.response) {
   throw new APIError(error.response.data.message || 'API Error', error.response.status);
  }
  throw new APIError('Network error', 500);
 }
}

export default function PillarCertification({
 surveyId,
 companyGroupId,
 formName,
 selectedCompanyId,
 isResponsibleUser = false,
}) {
 const { user } = useSSO();
 const toast = useToast();
 const isAudit = useIsAudit();
 const { downloadResponses, isLoadingResponses: isDownloadingResponses } =
  useResponsesDownload();

 const [isEvidenceModalOpen, setEvidenceModalOpen] = useState(false);
 const [currentQuestionId, setCurrentQuestionId] = useState();
 const [currentFiles, setCurrentFiles] = useState([]);
 const [selectedPillar, setSelectedPillar] = useState();
 const [isLoadingEvidence, setLoadingEvidence] = useState(false);
 const [isLoadingResponses, setLoadingResponses] = useState(false);
 const [certificationControlId, setCertificationControlId] = useState();
 const [isEditStatusModalOpen, setIsEditStatusModalOpen] = useState(false);
 const [isViewMode, setIsViewMode] = useState(false);

 const { data } = useSWR(
  surveyId && selectedCompanyId
   ? `survey/diagnostic/${surveyId}?id_company=${selectedCompanyId}`
   : null,
  fetchData
 );

 const { data: currentPillarData } = useSWR(
  selectedPillar
   ? `survey/admin/pillar/answered/certificate?id_company=${selectedCompanyId}&id_pillar=${selectedPillar}&id_survey=${surveyId}`
   : null,
  fetchData
 );

 const { data: statusData, mutate } = useSWR(
  certificationControlId ? `certificate-status-control/${certificationControlId}` : null,
  fetchData
 );

 async function fetchCertificationControlId(surveyId) {
  try {
   const response = await baseService.get(
    `certificate/link-certificate/certificate-status-control/${surveyId}`,
    {
     headers: {
      Accept: 'text/html,application/json',
     },
    }
   );

   return parseInt(response.data, 10);
  } catch (error) {
   console.error('Error fetching certification control ID:', error);
   throw error;
  }
 }

 useEffect(() => {
  if (data?.pillars?.length) {
   setSelectedPillar(data?.pillars[0]?.id_pillar);
  }

  if (surveyId && !certificationControlId) {
   fetchCertificationControlId(surveyId)
    .then((id) => {
     if (!isNaN(id)) {
      setCertificationControlId(id);
     }
    })
    .catch((error) => {
     console.error('Error fetching certification control ID:', error);
    });
  }
 }, [data, surveyId, certificationControlId]);

 const currentQuestions = useMemo(() => {
  if (!data) return [];
  if (currentPillarData?.pillar?.questions?.length) {
   return currentPillarData?.pillar?.questions || [];
  }
  return data?.survey?.questions || [];
 }, [data, currentPillarData]);

 // GRI-07: linha fantasma (question_response vazio, sem arquivo/medida) não
 // conta como respondida — senão o número sobe por indicador.
 const answeredQuestions = useMemo(() => {
  return currentQuestions.filter((question) =>
   respostaComConteudo(question, selectedCompanyId)
  ).length;
 }, [currentQuestions, selectedCompanyId]);

 const normalizedPillars = useMemo(() => {
  if (!data?.pillars?.length) return [];
  return data.pillars.map((pillar) => {
   const themes = pillar?.themes || [];
   return {
    id_pillar: pillar.id_pillar,
    name_pillar: pillar.name_pillar,
    img_pillar: pillar.img_pillar,
    totalQuestions: themes.reduce((sum, theme) => sum + (theme?.totalQuestions || 0), 0),
    answeredQuestions: themes.reduce((sum, theme) => sum + (theme?.answeredQuestions || 0), 0),
   };
  });
 }, [data]);

 const selectedPillarIndex = useMemo(() => {
  if (!selectedPillar || !data?.pillars) return 0;
  
  return data.pillars.findIndex(pillar => pillar.id_pillar === selectedPillar) + 1;
}, [selectedPillar, data?.pillars]);

 const hasEvidenceFiles = useMemo(() => {
  return currentQuestions.some((question) =>
   question.users_responses?.some(
    (response) =>
     response?.company?.id_company === +selectedCompanyId &&
     response?.response_file?.length > 0
   )
  );
 }, [currentQuestions, selectedCompanyId]);

 const handleQuestionEvidence = useCallback((questionId, response) => {
  setCurrentQuestionId(questionId);
  setCurrentFiles(response?.response_file);
  setEvidenceModalOpen(true);
 }, []);

 const handleCloseCurrentEvidence = () => {
  setEvidenceModalOpen(false);
  setCurrentQuestionId(null);
  setCurrentFiles([]);
 };

 const defaultValues = useMemo(() => {
  return currentQuestions.length ? initializeDefaultValues(currentQuestions) : {};
 }, [currentQuestions]);

 // EG-63: progresso do certificado inteiro (todos os pilares) no AuditHeader.
 const overallAnswered = useMemo(
  () => normalizedPillars.reduce((n, p) => n + (p.answeredQuestions || 0), 0),
  [normalizedPillars]
 );
 const overallTotal = useMemo(
  () => normalizedPillars.reduce((n, p) => n + (p.totalQuestions || 0), 0),
  [normalizedPillars]
 );

 useAuditHeaderData(
  isAudit
   ? {
      answered: overallTotal ? overallAnswered : answeredQuestions,
      total: overallTotal || currentQuestions.length,
      downloadingResponses: isDownloadingResponses,
      downloadingEvidence: isLoadingEvidence,
      evidenceDisabled: !hasEvidenceFiles,
      onViewAsRespondent: selectedPillar ? () => setIsViewMode(true) : undefined,
      onDownloadResponses: () =>
       downloadResponses({ surveyId, companyId: selectedCompanyId, companyGroupId, formName }),
      onDownloadEvidence: () => evidenceDownload(),
     }
   : null
 );

 async function evidenceDownload() {
  setLoadingEvidence(true);
  try {
   const response = await baseService.get(
    `/downloads?survey=${surveyId}&group=${companyGroupId}`,
    {
     responseType: 'blob',
     timeout: 30000,
    }
   );

   if (!response?.data) {
    throw new Error('No data received from server');
   }

   const blob = new Blob([response.data], { type: 'application/zip' });
   const url = window.URL.createObjectURL(blob);
   const link = document.createElement('a');
   link.href = url;
   link.download = `${formName}_evidence.zip`;

   document.body.appendChild(link);
   link.click();
   document.body.removeChild(link);
   window.URL.revokeObjectURL(url);

   toast({
    title: 'Download das evidências iniciado com sucesso',
    status: 'success',
   });
  } catch (error) {
   console.error('Evidence download error:', error);
   let errorMessage = 'Não foi possível processar os arquivos anexados.';

   if (error?.response?.status === 404) {
    errorMessage = 'Arquivos não encontrados.';
   } else if (error?.response?.status === 413) {
    errorMessage = 'Arquivos muito grandes para download.';
   } else if (error?.code === 'ECONNABORTED') {
    errorMessage = 'Tempo limite excedido. Tente novamente.';
   }

   toast({
    title: 'Erro ao fazer o download das evidências',
    description: errorMessage,
    status: 'error',
   });
  } finally {
   setLoadingEvidence(false);
  }
 }

 if (!data || !currentQuestions?.length) {
  return null;
 }

 if (isViewMode) {
  return (
   <RespondentViewPillar
    defaultValues={defaultValues}
    surveyId={surveyId}
    companyId={selectedCompanyId}
    userId={user?.user?.id_user}
    questions={currentQuestions}
    companyName={currentQuestions[0]?.company?.name}
    totalAnsweredQuestions={answeredQuestions}
    onClose={() => setIsViewMode(false)}
    currentPillarData={currentPillarData}
    pillarIndex={selectedPillarIndex}
    // certificationStatus={}
   />
  );
 }

 return (
  <Box>
   {isAudit ? (
    selectedPillar && (
     <Box mb='14px'>
      <PillarSelectorRow
       pillars={normalizedPillars}
       selectedPillar={selectedPillar}
       onSelect={setSelectedPillar}
      />
     </Box>
    )
   ) : (
   <Box bg='white' p='16px' borderRadius='12px'>
    <Flex h='63px' px='16px' bg='#F9F9F9' align='center' gap='12px' mb='16px'>
     <Flex gap='12px' h='31px' align='center' ml='auto'>
      <Text fontWeight={700}>Status:</Text>
      <StatusTag status={statusData?.status || 'não enviado'} />
     </Flex>

     {!isResponsibleUser &&
      statusData?.status &&
      statusData?.status !== 'não enviado' && (
       <Button
        variant='underline'
        fontWeight={600}
        fontSize={'15px'}
        onClick={() => setIsEditStatusModalOpen(true)}
       >
        Editar status
       </Button>
      )}
    </Flex>

    {!isResponsibleUser && (
     <Flex gap='5' overflowY='hidden' pos='absolute' right='32px' top='70px'>
      <DownloadResponsesButton
       surveyId={surveyId}
       companyId={selectedCompanyId}
       companyGroupId={companyGroupId}
       formName={formName}
       isLoadingResponses={isLoadingResponses}
       setLoadingResponses={setLoadingResponses}
       height='51px'
      />
      <EvidenceDownloadButton
       isLoading={isLoadingEvidence}
       isDisabled={!hasEvidenceFiles}
       onClick={evidenceDownload}
      />
     </Flex>
    )}

    <Flex align='center' justify='space-between' gap='6' mb={selectedPillar ? '16px' : 0}>
     <Heading as='h3' fontSize='md'>
      Questões
     </Heading>

     {!isResponsibleUser && selectedPillar && (
      <Button
       variant='underline'
       fontWeight={600}
       ml='auto'
       onClick={() => setIsViewMode(true)}
      >
       Visualizar como respondente
      </Button>
     )}
    </Flex>

    {selectedPillar && (
     <PillarSelectorRow
      pillars={normalizedPillars}
      selectedPillar={selectedPillar}
      onSelect={setSelectedPillar}
     />
    )}
   </Box>
   )}

   <Box mt={isAudit ? 0 : '6'}>
    {currentQuestions?.map((question, index) => (
     <QuestionCardSimple
      key={`${question?.id_question}_${index}`}
      question={question}
      index={index}
      selectedCompanyId={selectedCompanyId}
      handleQuestionEvidence={handleQuestionEvidence}
      surveyId={surveyId}
      userId={user?.user?.id_user}
      questionnaireType='certification'
     />
    ))}
   </Box>

   <EditStatusModal
    isOpen={isEditStatusModalOpen}
    onClose={() => setIsEditStatusModalOpen(false)}
    currentStatus={statusData?.status}
    certificateId={statusData?.id}
    onStatusUpdate={() => mutate()}
    certificateStatusControlId={statusData?.id}
   />

   {currentQuestionId && (
    <AttachEvidenceModal
     isOpen={isEvidenceModalOpen}
     onClose={handleCloseCurrentEvidence}
     questionId={currentQuestionId}
     files={currentFiles}
    />
   )}
  </Box>
 );
}
